/**
 * Main-thread handle on the puzzle worker.
 *
 * Falls back to generating in-place if workers are unavailable, so the app
 * still works rather than showing an empty grid -- it just janks for a moment.
 */

import { boardToString } from "../core/grid";
import { generateGraded } from "../core/grade";
import type { Tier } from "../core/techniques/types";
import type { PuzzlePayload, PuzzleRequest, PuzzleResponse } from "./puzzle.worker";

export type { PuzzlePayload };

let worker: Worker | null = null;
let nextId = 1;
const pending = new Map<number, { resolve: (p: PuzzlePayload) => void; reject: (e: Error) => void }>();

function ensureWorker(): Worker | null {
  if (worker) return worker;
  if (typeof Worker === "undefined") return null;
  try {
    worker = new Worker(new URL("./puzzle.worker.ts", import.meta.url), { type: "module" });
    worker.addEventListener("message", (event: MessageEvent<PuzzleResponse>) => {
      const entry = pending.get(event.data.id);
      if (!entry) return;
      pending.delete(event.data.id);
      if (event.data.ok) entry.resolve(event.data.puzzle);
      else entry.reject(new Error(event.data.error));
    });
    worker.addEventListener("error", () => {
      for (const entry of pending.values()) entry.reject(new Error("Puzzle worker failed."));
      pending.clear();
      worker = null;
    });
    return worker;
  } catch {
    return null;
  }
}

function generateHere(tier: Tier): PuzzlePayload {
  const result = generateGraded(tier, { maxAttempts: 600 });
  if (!result) throw new Error(`Could not find a ${tier} puzzle.`);
  return {
    puzzle: boardToString(result.puzzle),
    solution: boardToString(result.solution),
    tier: result.grade.tier,
    techniques: result.grade.techniques,
    seed: result.seed,
    clues: result.clues,
  };
}

export function requestPuzzle(tier: Tier): Promise<PuzzlePayload> {
  const active = ensureWorker();
  if (!active) {
    return new Promise((resolve, reject) => {
      try {
        resolve(generateHere(tier));
      } catch (error) {
        reject(error as Error);
      }
    });
  }

  const id = nextId++;
  return new Promise<PuzzlePayload>((resolve, reject) => {
    pending.set(id, { resolve, reject });
    active.postMessage({ id, tier } satisfies PuzzleRequest);
  });
}
